// =============================================================================
// Subscription Modal
// =============================================================================
// Modal for creating and managing investment subscriptions
// =============================================================================

import { useEffect, useState } from 'react';
import { XMarkIcon, BuildingOfficeIcon, CheckIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { Button } from '../../../components/ui';
import { fundraisingService } from '../../../services/fundraising.service';
import { getApiError } from '../../../services/api';
import type {
  AvailableDeal,
  SubscriptionWithDetails,
  CommitmentType,
} from '../../../types/fundraising';
import { DEAL_STATUS_LABELS } from '../../../types/fundraising';
import type { InvestorProfileType } from '../../../types';

// =============================================================================
// Types
// =============================================================================

interface SubscriptionModalProps {
  isOpen: boolean;
  contactId: string;
  contactName: string;
  existingSubscriptions: SubscriptionWithDetails[];
  onClose: () => void;
  onSubscriptionCreated: () => void;
}

// =============================================================================
// Constants
// =============================================================================

const ENTITY_TYPES: { value: InvestorProfileType; label: string }[] = [
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'LLC', label: 'LLC' },
  { value: 'TRUST', label: 'Trust' },
  { value: 'CORPORATION', label: 'Corporation' },
  { value: 'PARTNERSHIP', label: 'Partnership' },
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
];

// =============================================================================
// Component
// =============================================================================

export function SubscriptionModal({
  isOpen,
  contactId,
  contactName,
  existingSubscriptions,
  onClose,
  onSubscriptionCreated,
}: SubscriptionModalProps) {
  const [availableDeals, setAvailableDeals] = useState<AvailableDeal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState<string>('');
  const [commitmentAmount, setCommitmentAmount] = useState<string>('');
  const [commitmentType, setCommitmentType] = useState<CommitmentType>('SOFT');
  const [entityType, setEntityType] = useState<InvestorProfileType | ''>('');
  const [entityName, setEntityName] = useState('');
  const [entityState, setEntityState] = useState('');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load available deals when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAvailableDeals();
      resetForm();
    }
  }, [isOpen]);

  const resetForm = () => {
    setSelectedDealId('');
    setCommitmentAmount('');
    setCommitmentType('SOFT');
    setEntityType('');
    setEntityName('');
    setEntityState('');
    setNotes('');
    setError(null);
  };

  const loadAvailableDeals = async () => {
    setIsLoading(true);
    try {
      const deals = await fundraisingService.getAvailableDeals();
      setAvailableDeals(deals);
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDealId || !commitmentAmount) return;

    const amount = parseFloat(commitmentAmount.replace(/,/g, ''));
    if (isNaN(amount) || amount <= 0) {
      setError('Please enter a valid commitment amount');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await fundraisingService.createSubscription({
        contactId,
        dealId: selectedDealId,
        commitmentAmount: amount,
        commitmentType,
        entityType: entityType || undefined,
        entityName: entityName || undefined,
        entityState: entityState || undefined,
        notes: notes || undefined,
      });
      await onSubscriptionCreated();
      onClose();
    } catch (err) {
      setError(getApiError(err).message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter out deals that already have active subscriptions
  const activeSubscriptionDealIds = existingSubscriptions
    .filter((s) => !['CANCELLED', 'EXPIRED'].includes(s.status))
    .map((s) => s.dealId);

  const dealsWithoutSubscription = availableDeals.filter(
    (deal) => !activeSubscriptionDealIds.includes(deal.id)
  );

  // Format currency input
  const handleAmountChange = (value: string) => {
    // Remove non-numeric characters except decimal point
    const cleaned = value.replace(/[^0-9.]/g, '');
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) return;
    // Format with commas
    if (parts[0]) {
      parts[0] = parseInt(parts[0], 10).toLocaleString();
    }
    setCommitmentAmount(parts.join('.'));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Create Subscription</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <XMarkIcon className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <p className="text-sm text-gray-500 mb-4">
          Create a new investment subscription for{' '}
          <span className="font-medium text-gray-900">{contactName}</span>.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto" />
            <p className="mt-2 text-sm text-gray-500">Loading deals...</p>
          </div>
        ) : dealsWithoutSubscription.length === 0 ? (
          <div className="text-center py-8">
            <BuildingOfficeIcon className="h-12 w-12 text-gray-300 mx-auto" />
            <p className="mt-2 text-sm text-gray-500">
              {availableDeals.length === 0
                ? 'No deals available for subscriptions.'
                : 'This contact already has subscriptions for all available deals.'}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Deal Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Deal *</label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {dealsWithoutSubscription.map((deal) => (
                  <button
                    key={deal.id}
                    type="button"
                    onClick={() => setSelectedDealId(deal.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg border transition-colors text-left ${
                      selectedDealId === deal.id
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">{deal.name}</p>
                        <p className="text-xs text-gray-500">{DEAL_STATUS_LABELS[deal.status] || deal.status}</p>
                      </div>
                    </div>
                    {selectedDealId === deal.id && (
                      <CheckIcon className="h-5 w-5 text-primary-600" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Commitment Amount */}
            <div>
              <label htmlFor="commitmentAmount" className="block text-sm font-medium text-gray-700 mb-1">
                Commitment Amount *
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  id="commitmentAmount"
                  value={commitmentAmount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  placeholder="100,000"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            {/* Commitment Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Commitment Type *</label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="commitmentType"
                    value="SOFT"
                    checked={commitmentType === 'SOFT'}
                    onChange={() => setCommitmentType('SOFT')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Soft Commit</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="commitmentType"
                    value="HARD"
                    checked={commitmentType === 'HARD'}
                    onChange={() => setCommitmentType('HARD')}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Hard Commit</span>
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {commitmentType === 'SOFT'
                  ? 'Soft commit indicates interest but is non-binding'
                  : 'Hard commit is a binding commitment to invest'}
              </p>
            </div>

            {/* Entity Information (Optional) */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">Entity Information (Optional)</h3>

              <div className="space-y-3">
                <div>
                  <label htmlFor="entityType" className="block text-sm text-gray-600 mb-1">
                    Entity Type
                  </label>
                  <select
                    id="entityType"
                    value={entityType}
                    onChange={(e) => setEntityType(e.target.value as InvestorProfileType | '')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select type...</option>
                    {ENTITY_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="entityName" className="block text-sm text-gray-600 mb-1">
                    Entity Name
                  </label>
                  <input
                    type="text"
                    id="entityName"
                    value={entityName}
                    onChange={(e) => setEntityName(e.target.value)}
                    placeholder="e.g., Smith Family Trust"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="entityState" className="block text-sm text-gray-600 mb-1">
                    Entity State
                  </label>
                  <select
                    id="entityState"
                    value={entityState}
                    onChange={(e) => setEntityState(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">Select state...</option>
                    {US_STATES.map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
                Notes (Optional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Any additional notes about this subscription..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
              />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                variant="secondary"
                className="flex-1"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={isSubmitting || !selectedDealId || !commitmentAmount}
              >
                {isSubmitting ? 'Creating...' : 'Create Subscription'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default SubscriptionModal;
